import morph from "../helpers/morph";
import { returnPolicyPropertiesFromDataset } from "../helpers/returnPolicyProperties";
import {
  cartItemTracking,
  sourceFromDataset,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
} from "../helpers/dataLayer";
import "./cart-gifting";
import "./cart-upsell";
import "./stock-message";

let editorPromise = null;

/**
 * Fetch the monogram editor — the monogram modal and the Preact layer it needs,
 * which only a monogrammed cart ever uses. Loaded once, on the first edit.
 *
 * @param {HTMLElement} cartContent the element carrying the asset urls
 * @returns {Promise} resolves once window.openMonogramEditor is defined
 */
function loadMonogramEditor(cartContent) {
  // The cart page bundles this module twice — once for the page, once for the
  // drawer — so the loaded editor itself is the shared flag.
  if (window.openMonogramEditor) return Promise.resolve();
  if (editorPromise) return editorPromise;

  const src = cartContent.getAttribute("monogram-editor-script");
  const styles = cartContent.getAttribute("monogram-editor-styles");

  if (styles) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = styles;
    document.head.appendChild(link);
  }

  editorPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return editorPromise;
}

/**
 * Liquid serialises line item properties as an array of [key, value] pairs, but
 * the cart API takes an object.
 *
 * Liquid also hands back property values HTML-escaped (`"` as `&quot;`, `&` as
 * `&amp;`), so each value is decoded — otherwise JSON-valued properties like
 * `_monogram_data` don't parse, and re-added values come back double-escaped.
 *
 * @param {string} json properties as rendered by Liquid
 * @returns {object}
 */
function parseProperties(json) {
  try {
    const parsed = JSON.parse(json || "{}") || {};
    return Object.fromEntries(
      (Array.isArray(parsed) ? parsed : Object.entries(parsed)).map(
        ([key, value]) => [key, decodeEntities(value)],
      ),
    );
  } catch {
    return {};
  }
}

// DOMParser builds an inert document, so nothing in the value can run.
function decodeEntities(value) {
  if (typeof value !== "string" || !value.includes("&")) return value;
  return new DOMParser().parseFromString(value, "text/html").documentElement
    .textContent;
}

class CartContent extends HTMLElement {
  constructor() {
    super();
    this.requestId = 1;
    this.editing = false;

    this.addEventListener("click", (e) => {
      const item = e.target.closest("[data-item-key]");

      if (!item) return;

      const removeMonogramButton = e.target.closest("[data-remove-monogram]");

      if (removeMonogramButton) {
        this._removeMonogram(
          item.dataset.itemKey,
          removeMonogramButton.dataset,
          this._monogramTrackingItem(item, this._quantity(item)),
        );
        return;
      }

      const editMonogramButton = e.target.closest("[data-edit-monogram]");

      if (editMonogramButton) {
        this._editMonogram(item.dataset.itemKey, editMonogramButton);
        return;
      }

      if (!e.target.closest("[data-remove]")) return;

      const trackingItems = this._trackingItems(item, this._quantity(item));

      this._updateQty({ key: item.dataset.itemKey, quantity: 0 }, () =>
        trackRemoveFromCart(trackingItems),
      );
    });

    document.addEventListener("updateCart", (e) => {
      const requestId = e.detail.data?.requestId;

      // Debounce - cancel update if another request is already in progress.
      if (requestId === this.requestId || !requestId) {
        this._updateCart(e.detail.cart.sections[this.sectionId]);
      }
    });

    this.addEventListener("change", (e) => {
      const item = e.target.closest("[data-item-key]");
      if (!item) return;

      const quantity = parseInt(e.target.value, 10);
      const change = quantity - this._quantity(item);
      // Read now: the line may be repainted before the request settles.
      const trackingItems = this._trackingItems(item, Math.abs(change));

      // Only the latest request repaints the cart, so a burst of steps would
      // otherwise each be measured from the quantity before the first.
      item.dataset.quantity = quantity;

      this._updateQty({ key: item.dataset.itemKey, quantity }, () => {
        if (change > 0) trackAddToCart(trackingItems);
        if (change < 0) trackRemoveFromCart(trackingItems);
      });
    });
  }

  connectedCallback() {
    if (this.hasAttribute("track-view")) this.trackView();
  }

  /**
   * Push dl_view_cart for the cart as shown, each monogram its own product.
   */
  trackView() {
    const items = [...this.querySelectorAll("[data-item-key]")].flatMap(
      (item) => this._trackingItems(item, this._quantity(item)),
    );

    trackViewCart(items, this.dataset.dlCartTotal / 100);
  }

  _updateQty({ key, quantity }, onAfterAdd) {
    window.changeCart(
      { id: key, quantity },
      {
        onError: () => this._refreshCart(),
        onAfterAdd,
        data: { requestId: ++this.requestId },
        sections: [this.sectionId],
      },
    );
  }

  _quantity(item) {
    return parseInt(item.dataset.quantity, 10);
  }

  /**
   * Some of a line, as trackAddToCart and trackRemoveFromCart take it: the
   * line's product, plus its monogram when it has one.
   *
   * @param {HTMLElement} item the line's [data-item-key] element
   * @param {number} quantity how many of the line
   */
  _trackingItems(item, quantity) {
    const monogram = this._monogramTrackingItem(item, quantity);

    return [
      {
        quantity,
        tracking: cartItemTracking(sourceFromDataset(item.dataset), {
          hasMonogram: !!monogram,
        }),
      },
      monogram,
    ].filter(Boolean);
  }

  _monogramTrackingItem(item, quantity) {
    const monogram = item.querySelector(
      ".cart-item__component[data-dl-product-id]",
    );
    if (!monogram) return null;

    return {
      quantity:
        quantity * (parseInt(monogram.dataset.dlQuantityPerItem, 10) || 1),
      tracking: cartItemTracking(sourceFromDataset(monogram.dataset)),
    };
  }

  /**
   * Drop a monogram: the product comes back on its own, at the quantity it had.
   * Tracked as the monogram alone leaving, though the whole bundle is swapped.
   *
   * @param {object} trackingItem the monogram, as _monogramTrackingItem builds it
   */
  _removeMonogram(key, dataset, trackingItem) {
    const { variantId, quantity, properties } = dataset;

    this._replaceBundle(
      key,
      [
        {
          id: variantId,
          quantity: parseInt(quantity, 10) || 1,
          properties: {
            ...this._productProperties(properties),
            ...returnPolicyPropertiesFromDataset(dataset),
          },
        },
      ],
      () => trackingItem && trackRemoveFromCart([trackingItem]),
    );
  }

  /**
   * Reopen the monogram modal on a configured monogram. The cart stays plain
   * Liquid — the Preact layer is only fetched and mounted once the shopper asks
   * to edit — and hands back the replacement lines when they add the monogram.
   *
   * The drawer is closed while the modal is up: it would otherwise close itself
   * on the first click inside the modal, and the two would fight over the focus
   * trap. It reopens on the way out, updated.
   */
  _editMonogram(key, button) {
    if (this.editing) return;
    this.editing = true;

    const drawer = this.closest("mini-cart");
    const drawerOpen = Boolean(drawer?.hasAttribute("open"));

    // The editor is fetched, then the product and monogram component behind it,
    // so the button carries the wait.
    const label = button.querySelector("[data-label]");
    const editLabel = label?.textContent;
    if (label) label.textContent = button.dataset.loadingLabel;
    button.setAttribute("aria-busy", "true");

    const done = () => {
      this.editing = false;
      if (label) label.textContent = editLabel;
      button.removeAttribute("aria-busy");
    };

    const onClosed = () => {
      done();
      if (drawerOpen) document.dispatchEvent(new CustomEvent("mini-cart:open"));
    };

    loadMonogramEditor(this)
      .then(() => {
        if (drawerOpen) {
          document.dispatchEvent(new CustomEvent("mini-cart:close"));
        }

        window.openMonogramEditor({
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
          monogramHandle: button.dataset.monogramHandle,
          quantity: parseInt(button.dataset.quantity, 10) || 1,
          properties: this._productProperties(button.dataset.properties),
          monogramProperties: parseProperties(
            button.dataset.monogramProperties,
          ),
          returnFocus: button,
          onApply: (items) => this._replaceBundle(key, items),
          onClosed,
        });
      })
      .catch((error) => {
        console.warn("[monogram] Unable to load the monogram editor.", error);
        done();
      });
  }

  /**
   * The product line's properties as they should be re-added: everything the
   * shopper's line carried, minus the bundle plumbing, which is regenerated for
   * whatever replaces it.
   */
  _productProperties(json) {
    const properties = parseProperties(json);

    delete properties._uid;
    delete properties._monogram_summary;
    // The monogram is what put the line out of returns; whether the product is
    // out of returns on its own is decided again by whatever replaces it.
    delete properties._non_returnable;

    return properties;
  }

  /**
   * Swap a monogrammed bundle for the given lines. A monogram can't be changed
   * or removed on its own — it's a component of a bundle line — so the whole
   * bundle goes and its replacement is added in one go. The removal is tagged
   * with an already-stale request id so the cart only paints once, after the
   * replacement is in.
   *
   * Neither the removal nor the re-add is tracked as such — the caller decides
   * what, if anything, the swap amounts to, via `onRemoved`.
   */
  _replaceBundle(key, items, onRemoved) {
    const staleRequestId = this.requestId++;

    window.changeCart(
      { id: key, quantity: 0 },
      {
        onError: () => this._refreshCart(),
        data: { requestId: staleRequestId },
        onAfterAdd: () => {
          onRemoved?.();
          window.addToCart(items, {
            tracking: false,
            onError: () => this._refreshCart(),
            data: { requestId: ++this.requestId },
          });
        },
      },
    );
  }

  _refreshCart() {
    window
      .fetchPageData({
        path: "/cart",
        options: { sectionId: this.sectionId },
      })
      .then((markup) => this._updateCart(markup));
  }

  _updateCart(markup) {
    if (!markup) return;

    const html = new DOMParser().parseFromString(markup, "text/html");
    const sourceContent = html.querySelector("cart-content");
    if (!sourceContent) return;

    // The server always renders the swatch accordion closed, so carry the
    // shopper's current state across the morph.
    const swatchesOpen = this.querySelector("[data-swatch-details]")?.open;

    morph(this, sourceContent);

    if (!swatchesOpen) return;

    const swatches = this.querySelector("[data-swatch-details]");
    if (swatches && !swatches.open) {
      swatches.open = true;
      swatches.classList.add("open");
    }
  }

  get sectionId() {
    return this.getAttribute("cart-section-id");
  }
}

if (!customElements.get("cart-content")) {
  customElements.define("cart-content", CartContent);
}
